import React, { useState, useEffect, useMemo, useRef } from 'react';
import type { Upload } from 'antd';
import type { UploadFile } from 'antd/es/upload/interface';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { sendStepCompleteEmail } from '../../../UtilsEmail';

import {
  trackClickOpenMAPage,
  trackUploadSample,
  trackUploadFileSuccess,
  trackUploadBatchSuccess,
  trackUploadError,
  trackClickUploadMore,
  trackPeopleClickCheckInLink,
  trackPeopleToggleUseFileName,
  trackPeopleToggleDetails,
} from '../../../analytics';

import { SellingPoint, InfoPoints } from '../../../components/custom';
import PhotoRules from '../../../components/custom/photoRules';
import { Switch } from '../../../components/general';
import {
  PageWrapper,
  PageHeader,
  PageSubHeader,
  BackBtn,
} from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL } from '../../../constants';

import useSampleProjectData from '../../../hooks/useSampleProjectData';

import { getCreationResults } from '../../../services/api/creations';
import { createFace, getUploadedFaces } from '../../../services/api/faces';
import { uploadFile } from '../../../services/uploadFileService';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';

import { Person, PageError } from '../../../types';
import { isEmpty, getUrl, getShareLink } from '../../../utils';

import AssetsUpload from '../components/assetsUpload';
import DoneBtn from '../components/doneBtn';
import PageWithSideBar from '../components/pageWithSideBar';
import { UploadStage, UploadingPeople } from '../components/types';
import UploadMoreBtn from '../components/uploadMoreBtn';
import getPeopleText from './components/getPeopleText';
import PeopleSummary from './components/peopleSummary';
import SampleComponent from './components/sampleComponent';

const People = () => {
  const { eventId = '' } = useParams();

  const { user } = useUserStore();
  const {
    eventId: eventIdLocal,
    currentProject,
    projectError,
    setEventId,
    setSampleProject,
  } = useProjectStore();

  const [uploadedPeople, setUploadedPeople] = useState<Person[]>([]);
  const [uploadingPeople, setUploadingPeople] = useState<UploadingPeople>({});
  const [generatedPeopleIds, setGeneratedPeopleIds] = useState<string[]>([]);

  const [showUpload, setShowUpload] = useState(false); // whether upload area is shown (user clicked "upload more")
  const [isUseFileName, setIsUseFileName] = useState(false);
  const [isShowDetails, setIsShowDetails] = useState(false);
  const [deletingPeopleIds, setDeletingPeopleIds] = useState<string[]>([]);
  const [attendeeNum, setAttendeeNum] = useState(uploadedPeople.length ?? 0);

  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<PageError>(null);
  const [error, setError] = useState('');

  const uploadRef = useRef<React.ElementRef<typeof Upload>>(null);

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);
  const samplePeople = sampleProjectData.people || [];

  // assignee data
  const assigneeData = JSON.parse(localStorage.getItem('assigneeData') ?? '{}');
  const isPublic = currentProject?.isAssigneeAccess ?? false;

  // set event id on mount
  useEffect(() => {
    if (isSample) setSampleProject(eventId);
    else if (eventIdLocal !== eventId) setEventId(eventId, true);
  }, [eventId, eventIdLocal, isSample, setSampleProject, setEventId]);

  // fetch data on mount
  useEffect(() => {
    const onMount = async () => {
      setIsLoading(true);

      if (
        currentProject?.isAssigneeAccess &&
        !assigneeData[currentProject?.eventId]?.accessPages.includes('people')
      ) {
        setPageError(403);
        setIsLoading(false);
        return;
      }

      await fetchPeopleData();
      await fetchCreationsData();

      setIsLoading(false);
    };

    if (projectError) {
      setPageError(projectError);
      setIsLoading(false);
      return;
    }

    if (currentProject)
      if (isSample) {
        if (sampleProjectData.isPeopleUploaded) setUploadedPeople(samplePeople);
        setIsLoading(false);
      } else onMount();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, projectError]);

  const fetchPeopleData = async (checkPhotos?: boolean) => {
    try {
      const people = await getUploadedFaces(eventId, isPublic);
      setUploadedPeople(people);

      if (checkPhotos) {
        const hasAllPhotos = people.every(
          (person: Person) =>
            isEmpty(person.files) ||
            person.files?.every((file) => file.mediaStoreCompleted),
        );

        // if some photos are still uploading – re-fetch every second
        if (!hasAllPhotos) setTimeout(() => fetchPeopleData(true), 1000);
      }
    } catch (err) {
      console.error(`Error fetching the people: ${err}`);
      setError('Error fetching the people');
    }
  };

  const fetchCreationsData = async () => {
    try {
      const creations = await getCreationResults(eventId, isPublic);
      setGeneratedPeopleIds(creations.map((p) => p.face.elementId));
    } catch (err) {
      console.error(`Error fetching the creations: ${err}`);
      setError('Error fetching the creations');
    }
  };

  const { newPeople, generatedPeople } = useMemo(() => {
    const newPeople: Person[] = [];
    const generatedPeople: Person[] = [];

    uploadedPeople.forEach((person) => {
      if (generatedPeopleIds.includes(person.face.elementId)) {
        person.shareLink = getShareLink(eventId, person.face.elementId);
        generatedPeople.push(person);
      } else newPeople.push(person);
    });

    return { newPeople, generatedPeople };
  }, [eventId, uploadedPeople, generatedPeopleIds]);

  // compute current stage: to-upload | uploading | summary
  const stage: UploadStage = useMemo(() => {
    if (!isEmpty(uploadingPeople)) return 'uploading';
    if (isEmpty(uploadedPeople) || showUpload) return 'to-upload';
    return 'summary';
  }, [uploadingPeople, uploadedPeople, showUpload]);

  const prevStageRef = useRef(stage);

  const handleDrop = (file: UploadFile, parentElementId: string, now: Date) => {
    setShowUpload(false);

    // add person to uploading list
    setUploadingPeople((prevPeople) => ({
      ...prevPeople,
      [file.uid]: {
        face: {
          id: file.uid,
          parentElementId,
          name: file.name,
          created: now,
          progress: 0,
        },
      },
    }));
  };

  const handleComplete = async (file: UploadFile) => {
    await fetchPeopleData();

    // remove person from uploading list
    setUploadingPeople((prevPeople) => {
      const uploadingPeopleTemp = { ...prevPeople };
      delete uploadingPeopleTemp[file.uid];
      return uploadingPeopleTemp;
    });
  };

  const handleError = (file: UploadFile) => {
    console.error(error);
    trackUploadError('people', file.name);
    toast.error(`${file.name} file upload failed.`);
  };

  const handleUploadPerson = async (
    file: UploadFile,
    fileList: UploadFile[],
  ) => {
    try {
      const now = new Date();

      // person details
      const parentElementId = uuidv4();
      const index = fileList.indexOf(file);
      const num = attendeeNum + index + 1;

      setAttendeeNum(num);

      handleDrop(file, parentElementId, now);

      let name = `Attendee ${num}`;
      if (isUseFileName) {
        const lastDot = file.name.lastIndexOf('.');
        name = lastDot === -1 ? file.name : file.name.slice(0, lastDot);
        name = name.replaceAll('.', ' ');
      }

      const personData = {
        elementId: parentElementId,
        name,
        created: now,
        modified: now,
      };

      // upload person image
      uploadFile({ file, eventId, parentElementId, isPublic })
        // when upload is complete – create person
        .then(async () => {
          await createFace(eventId, personData, isPublic);

          handleComplete(file);

          trackUploadFileSuccess('people', eventId);
        });
    } catch (error) {
      handleError(file);
    }

    return false;
  };

  // when upload completes
  useEffect(() => {
    const prevStage = prevStageRef.current;

    if (prevStage === 'uploading' && stage === 'summary') {
      toast.success('All people are uploaded!', {
        toastId: 'people-upload-success',
      });

      localStorage.setItem(
        'uploadData',
        JSON.stringify({
          ...JSON.parse(localStorage.getItem('uploadData') ?? '{}'),
          peopleCount: (isSample ? samplePeople : uploadedPeople).length,
        }),
      );
      window.dispatchEvent(new Event('storage'));

      // for sample – track people upload with num of selected people
      if (isSample) trackUploadSample('people', samplePeople.length);
      else {
        // send email to the user that the upload is complete
        if (!isPublic && user && currentProject)
          sendStepCompleteEmail(user, currentProject, 'People upload');

        trackUploadBatchSuccess('people', eventId);
      }
    }

    prevStageRef.current = stage;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleUploadMore = (
    e: React.MouseEvent<HTMLElement>,
    position: string,
  ) => {
    setShowUpload(true);
    setTimeout(() => uploadRef.current?.upload?.uploader?.onClick(e));
    trackClickUploadMore('people', position, isSample);
  };

  const hasMissingPhotos = useMemo(() => {
    return newPeople.some((person) =>
      isEmpty(person.files?.filter((file) => file.fileType === 'image')),
    );
  }, [newPeople]);

  const { subheading, infoPoints } = getPeopleText({
    stage,
    peopleCount: uploadedPeople.length,
    isPublic,
    isSample,
    handleUploadMore,
  });

  const Toggle = () => {
    if (isSample) return null;

    if (stage === 'to-upload')
      return (
        <div className="flex items-center gap-2">
          <div className="text-sm">Use filename as name:</div>

          <Switch
            checked={isUseFileName}
            onChange={() => {
              setIsUseFileName(!isUseFileName);
              trackPeopleToggleUseFileName(!isUseFileName);
            }}
            size="sm"
          />
        </div>
      );

    if (stage === 'summary')
      return (
        <div className="flex items-center gap-2">
          <div className="text-sm">Show details:</div>

          <Switch
            checked={isShowDetails}
            onChange={() => {
              setIsShowDetails(!isShowDetails);
              trackPeopleToggleDetails(!isShowDetails);
            }}
            size="sm"
          />
        </div>
      );

    return null;
  };

  return (
    <PageWrapper
      type={isPublic ? 'header-only' : 'project'}
      title={`People – ${currentProject?.name}`}
      isLoading={isLoading}
      pageError={pageError}
    >
      <BackBtn label="Video Builder" />

      <PageWithSideBar
        mainContent={
          <>
            <PageHeader
              heading="People"
              subheading={subheading}
              icons={<Toggle />}
            />

            {stage === 'to-upload' ? (
              // to upload view
              <React.Fragment>
                <SampleComponent
                  setUploadedPeople={setUploadedPeople}
                  setUploadingPeople={setUploadingPeople}
                />

                <AssetsUpload
                  type="people"
                  uploadRef={uploadRef}
                  beforeUpload={handleUploadPerson}
                  isSample={isSample}
                />
              </React.Fragment>
            ) : (
              // uploading/summary view
              <React.Fragment>
                <PeopleSummary
                  people={newPeople}
                  uploadingPeople={uploadingPeople}
                  deletingPeopleIds={deletingPeopleIds}
                  setDeletingPeopleIds={setDeletingPeopleIds}
                  fetchPeopleData={fetchPeopleData}
                  isPublic={isPublic}
                  isShowDetails={isShowDetails}
                  isSample={isSample}
                />

                {!isEmpty(generatedPeople) && (
                  <React.Fragment>
                    <PageSubHeader
                      heading="Already generated"
                      subheading={
                        <div>
                          The people below already have their personalised
                          videos generated. Their videos will not be
                          re-generated.{' '}
                          <Link to={getShareLink(eventId)} target="_blank">
                            View their videos
                          </Link>
                          .
                        </div>
                      }
                    />

                    <PeopleSummary
                      people={generatedPeople}
                      deletingPeopleIds={deletingPeopleIds}
                      setDeletingPeopleIds={setDeletingPeopleIds}
                      fetchPeopleData={fetchPeopleData}
                      isPublic={isPublic}
                      isShowDetails={isShowDetails}
                    />
                  </React.Fragment>
                )}

                {stage === 'summary' && (
                  <React.Fragment>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <DoneBtn addClass={!isSample ? 'sm:w-1/2' : ''} />

                      {!isSample && (
                        <UploadMoreBtn
                          onClick={(e: React.MouseEvent<HTMLElement>) =>
                            handleUploadMore(e, 'Bottom button')
                          }
                        >
                          Check-In More People
                        </UploadMoreBtn>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
          </>
        }
        sideContent={
          <>
            {!isSample && (
              <SellingPoint
                text={
                  <>
                    Tip: Share{' '}
                    <Link
                      className="text-white"
                      to={getUrl(URL.CHECK_IN, eventId)}
                      onClick={() => trackPeopleClickCheckInLink('People')}
                      target="_blank"
                    >
                      this link
                    </Link>{' '}
                    to allow people to self check-in.
                  </>
                }
              />
            )}

            <InfoPoints points={infoPoints} isInfo={stage === 'summary'} />

            {stage === 'summary' && hasMissingPhotos && !isSample && (
              <div>
                Looks like some people are missing a photo. You can upload them
                now or{' '}
                <Link
                  to={getUrl(URL.PROJECT_MISSING_ASSETS, eventId)}
                  onClick={() => trackClickOpenMAPage('people')}
                >
                  email attendees
                </Link>{' '}
                to complete their check-in.
              </div>
            )}
          </>
        }
        sampleTipType={isSample ? stage : undefined}
      />

      {stage === 'to-upload' && <PhotoRules className="mt-6 lg:mt-10" />}

      <ErrorModal
        show={!!error}
        heading="Something went wrong"
        subheading={error}
        isTryAgainBtn
      />
    </PageWrapper>
  );
};

export default People;
